import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  ListDivider,
  Modal,
  ModalDialog,
  ModalClose,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { Plus as AddIcon } from '@phosphor-icons/react';
import type { ContactSource } from '../../../types/crm';

interface LeadSourcePickerProps {
  value: number | undefined;
  onChange: (id: number | undefined) => void;
  availableSources: ContactSource[];
  onCreateSource: (name: string) => Promise<ContactSource>;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
}

const CREATE_SENTINEL = '__create_lead_source__';

export const LeadSourcePicker: React.FC<LeadSourcePickerProps> = ({
  value,
  onChange,
  availableSources,
  onCreateSource,
  label = 'Lead Source',
  placeholder = 'Select a source',
  helperText,
  error = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Always include the current value's source in the option list, even if it
  // hasn't propagated into availableSources yet (e.g. immediately after async
  // create). This keeps the Select able to render the selected label.
  const selectedSource =
    value !== undefined
      ? availableSources.find((s) => s.id === value)
      : undefined;

  const sortedSources = [...availableSources].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const handleSelectChange = (newValue: string | null) => {
    if (newValue === null || newValue === '') {
      onChange(undefined);
      return;
    }
    if (newValue === CREATE_SENTINEL) {
      setNewName('');
      setCreateError(null);
      setShowModal(true);
      return;
    }
    onChange(Number(newValue));
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setCreateError('Name is required');
      return;
    }
    if (
      availableSources.some(
        (s) => s.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      setCreateError('A source with that name already exists');
      return;
    }
    try {
      setIsCreating(true);
      setCreateError(null);
      const created = await onCreateSource(name);
      onChange(created.id);
      setShowModal(false);
      setNewName('');
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : 'Failed to create source'
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <FormControl error={error}>
        <FormLabel>{label}</FormLabel>
        <Select
          placeholder={placeholder}
          value={value !== undefined ? String(value) : ''}
          onChange={(_, v) => handleSelectChange(v)}
          renderValue={() =>
            selectedSource ? (
              <Typography level="body-sm">{selectedSource.name}</Typography>
            ) : null
          }
        >
          <Option value={CREATE_SENTINEL}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--brand-text, #7013f5)' }}>
              <AddIcon size={18} />
              <Typography level="body-sm" sx={{ color: 'var(--brand-text, #7013f5)', fontWeight: 600 }}>
                Create new source...
              </Typography>
            </Box>
          </Option>
          <ListDivider />
          {sortedSources.map((source) => (
            <Option key={source.id} value={String(source.id)}>
              {source.name}
            </Option>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      <Modal open={showModal} onClose={() => !isCreating && setShowModal(false)}>
        <ModalDialog sx={{ minWidth: 360 }}>
          <ModalClose disabled={isCreating} />
          <Typography level="title-md">Create Lead Source</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl error={!!createError}>
              <FormLabel>Name</FormLabel>
              <Input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (createError) setCreateError(null);
                }}
                placeholder="e.g. Yelp Pro, Bing Ads"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                disabled={isCreating}
              />
              {createError && <FormHelperText>{createError}</FormHelperText>}
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setShowModal(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                loading={isCreating}
                disabled={!newName.trim()}
              >
                Create
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};
